// Question definitions for the SCS "Safety in Special Groups & Situations"
// wizard.
//
// Mirrors scsAnalysisOfAdverseEventsQuestions / scsClinicalLaboratoryEvaluations
// Questions — the three pages ask the same shaped questions (what to report on,
// which table, then one or more comparison groupings), so they share attribute
// names wherever the answer is the same answer: `number` is the stable badge
// shown in the CSR-style index badge (page order, hardcoded — it does not
// renumber as conditional sections toggle), `text` is the fallback wording and
// `tooltip` holds the ticket INFORMATION/helper text (the endpoint returns
// wording only, no tooltip map).
//
// At runtime the wording is overridden by
// GET /clinical_summaries/safety_summary/questions, keyed by the same attribute
// the answer is stored under. `textKeys` lists the further attributes to read
// that wording from when the endpoint carries the question under another name —
// the comparison questions (Q5–Q7) are the Description of Studies questions
// repeated per grouping, so they reuse that page's wording and only their badge
// number changes.

export const SCS_SISG_QUESTIONS = {
  qSpecialSubgroups: {
    number: 1,
    text: "Which Safety subgroups, additional to gender, age and race, do you want to report on?",
    tooltip:
      "Gender, Age and Race are always reported and cannot be removed. Add any further subgroup by typing it in or picking it from the suggestions (e.g. alcohol use, smoking, liver disease, renal disease, drug interactions).",
  },
  qSpecialSubgroupTable: {
    number: 2,
    required: true,
    text: "What Safety subgroup table do you want to report on?",
    tooltip: "One table per subgroup.",
  },
  qSpecialSection1Groupings: {
    number: 3,
    text: "Which groupings do you want to compare? Select the groupings from the Description of Studies page to include for this subgroup.",
  },
  qSpecialAdditionalGroupingName: {
    number: 4,
    text: "Which additional groupings do you want to compare? Name the grouping and then select the studies in this grouping.",
  },
  qSpecialComparisonType: {
    number: 5,
    // The endpoint has no wording of its own for this question yet; it is the
    // same question as Q7 of the Description of Studies page.
    textKeys: ["qLabComparisonType", "qComparisonType"],
    text: "How is the grouping compared?",
  },
  // Q6 / Q7 below are the Description of Studies branch questions (its Q8 / Q9)
  // shown for the comparison type picked in Q5. The Non-Comparative names and
  // the Comparative treatment groups are two different questions on screen but
  // are saved under the same qSpecialTreatmentGroups attribute (a grouping only
  // ever has one comparison type), so the Non-Comparative one keeps its own
  // key here.
  qSpecialNonComparativeGroupNames: {
    number: 6,
    textKeys: ["qNonComparativeGroupNames"],
    text: "What are the actual names used to describe the treatment/analysis groups in the column headers?",
  },
  qSpecialTreatmentGroups: {
    number: 6,
    textKeys: ["qTreatmentGroupNames"],
    text: "What are the actual names used to describe the treatment/analysis groups in the column headers? Please define the role for each column name.",
  },
  qSpecialComparisonPairs: {
    number: 7,
    textKeys: ["qComparisonPairs"],
    text: "Which dose/treatment groups should be compared?",
  },
  qSpecialDoseGroups: {
    number: 6,
    textKeys: ["qDoseGroups"],
    text: "Please list the dose group column headers from lowest to highest, starting with Placebo if present.",
  },
};

// Comparison type (Q5) -> the value it is saved as. The question is answered
// with the on-screen wording (the same button group as Q7 of the Description of
// Studies page) but persisted as the backend's grouping kind, which also decides
// which branch answers a grouping carries:
//   Non-Comparative -> single_arm    qSpecialTreatmentGroups (plain names)
//   Comparative     -> multi_arm     qSpecialTreatmentGroups ({ name, role })
//                                    + qSpecialComparisonPairs
//   Dose-Finding    -> dose_finding  qSpecialDoseGroups
export const SCS_SISG_COMPARISON_TYPE_VALUES = {
  "Non-Comparative": "single_arm",
  Comparative: "multi_arm",
  "Dose-Finding": "dose_finding",
};

// Saved value -> the on-screen wording, for re-populating Q5 from saved data.
export const SCS_SISG_COMPARISON_TYPE_LABELS = Object.fromEntries(
  Object.entries(SCS_SISG_COMPARISON_TYPE_VALUES).map(([label, value]) => [
    value,
    label,
  ])
);

// Q1 always reports on these three subgroups: they are pre-populated and locked
// (shown as chips without a remove control), so the answer is always these plus
// whatever the user adds.
export const SCS_SPECIAL_LOCKED_SUBGROUPS = ["Gender", "Age", "Race"];

// Suggestions offered by Q1 on top of the free text the user can type in.
//
// TODO: the questions endpoint carries no option list for these yet, so the
// suggestions are hardcoded here — swap them for the served list once it is
// available.
export const SCS_SPECIAL_SUBGROUP_SUGGESTIONS = [
  "Alcohol use",
  "Smoking",
  "Liver disease",
  "Renal disease",
  "Drug interactions",
];
